import { StateService } from "@uirouter/angular";
import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { Observable, take } from "rxjs";
import dayjs from "@gtmhub/dayjs";
import { AssigneeType } from "@webapp/assignees/models/assignee.models";
import { AssigneesFacade } from "@webapp/assignees/services/assignees/assignees-facade.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { GoalsFacade } from "@webapp/okrs/goals/services/goals-facade.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { DropdownMenuItem } from "@webapp/shared/dropdown/dropdown.models";
import { DropdownMenuItemBuilder } from "@webapp/shared/dropdown/util/dropdown-menu-item-builder";
import { Task } from "@webapp/tasks/models/tasks.models";
import { TasksFacade } from "@webapp/tasks/services/task-facade.service";
import { ExternalSystem } from "../../okr-tasks.models";

@Component({
  selector: "okr-task-row",
  templateUrl: "okr-task-row.component.html",
  styleUrls: ["okr-task-row.component.less"],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [DestroyService],
})
export class OkrTaskRowComponent implements OnInit {
  @Input()
  public task: Task;

  @Input()
  public item: Goal | Metric;

  public dropdownOptions: DropdownMenuItem[] = [];
  public isTaskDeleted = false;
  public isCalendarOpen = false;
  public dueDate: string;
  public hasManageTasksPermission$: Observable<boolean>;
  public confirmDelete = {
    on: false,
  };
  public externalSystem: ExternalSystem;

  constructor(
    private tasksFacade: TasksFacade,
    private broadcastService: BroadcastService,
    private goalsFacade: GoalsFacade,
    private metricsFacade: MetricsFacade,
    private permissionsFacade: PermissionsFacade,
    private stateService: StateService,
    private assigneesFacade: AssigneesFacade
  ) {}

  public ngOnInit(): void {
    this.dropdownOptions = this.createDropdownOptions();
    this.dueDate = this.task.dueDate;
    this.hasManageTasksPermission$ = this.permissionsFacade.hasPermission$("ManageTasks");
    if (this.task.externalSystem) {
      this.externalSystem = { name: this.task.externalSystem, id: this.task.externalId, metadata: this.task.metadata };
    }
  }

  public ownerAssigneeType = (task: Task): AssigneeType => {
    const assigneesIdMap = this.assigneesFacade.getAssigneesIdMap();
    return assigneesIdMap[task.ownerId]?.type;
  };

  public getOwnerName = (ownerId: string): string => {
    const assigneesIdMap = this.assigneesFacade.getAssigneesIdMap();
    return assigneesIdMap[ownerId]?.name;
  };

  public goToTask = (): void => {
    this.stateService.go("._task.task", { taskId: this.task.id });
  };

  public goToEmployee = (): void => {
    this.stateService.go("gtmhub.employees.employee", { employeeId: this.task.ownerId });
  };

  private createDropdownOptions = (): DropdownMenuItem[] => {
    const menuItemBuilder = new DropdownMenuItemBuilder();

    const showDetailsItem = menuItemBuilder
      .setKey("show_details")
      .setSkipFromTabOrder()
      .setAction({ state: "._task.task", params: { taskId: this.task.id } })
      .build();

    const archiveItem = menuItemBuilder
      .setKey("archive")
      .setSkipFromTabOrder()
      .setAction({ handler: (): void => this.archiveTask(this.task) })
      .build();

    const cloneItem = menuItemBuilder
      .setKey("clone")
      .setSkipFromTabOrder()
      .setAction({ handler: (): void => this.clone(this.task) })
      .build();

    const deleteItem = menuItemBuilder
      .setKey("delete")
      .setSkipFromTabOrder()
      .setAction({
        handler: (): void => {
          this.confirmDelete.on = true;
        },
      })
      .setToBeDestructive()
      .build();

    return [showDetailsItem, archiveItem, cloneItem, deleteItem];
  };

  public archiveTask(task: Task): void {
    const partialTask = { archived: true };
    this.tasksFacade
      .update$(task.id, partialTask)
      .pipe(take(1))
      .subscribe((task) => {
        this.broadcastService.emit("taskUpdated", { task: task, update: partialTask, sessionId: this.item.sessionId });
      });
  }

  public delete = (): void => {
    this.tasksFacade
      .deleteTask$(this.task.id)
      .pipe(take(1))
      .subscribe(() => {
        this.isTaskDeleted = true;
        if (this.task.parentType === "metric") {
          this.metricsFacade
            .getAll$({ filter: { _id: this.task.parentId } })
            .pipe(take(1))
            .subscribe((parentMetric) => {
              this.broadcastService.emit("taskDeleted", { task: this.task, sessionId: parentMetric.items[0].sessionId });
            });
        } else {
          // defaults to parent type 'goal'
          this.goalsFacade
            .getAll$({ filter: { _id: this.task.parentId }, fields: ["sessionId"] })
            .pipe(take(1))
            .subscribe((parentGoal) => {
              this.broadcastService.emit("taskDeleted", { task: this.task, sessionId: parentGoal.items[0].sessionId });
            });
        }
      });
  };

  public setDueDate = (date: string): void => {
    date = dayjs(date).format();

    this.dueDate = date;
    this.task.dueDate = date;

    this.update("dueDate");
  };

  public removeDueDate = (): void => {
    this.task.unset = {
      dueDate: true,
    };
    this.dueDate = "";
    this.task.dueDate = "";

    this.update("unset");
  };

  public changeStatus = (status: string): void => {
    this.task.status = status;
    this.update("status");
  };

  public clone = (taskForClone: Task): void => {
    const clonedTask = angular.copy(taskForClone);
    this.tasksFacade
      .create$(clonedTask, { cloned: true })
      .pipe(take(1))
      .subscribe((newTask) => {
        this.broadcastService.emit("taskCreated", { task: newTask, sessionId: undefined });
      });
  };

  private update = (field: string): void => {
    const update: Partial<Task> = { [field]: this.task[field] };

    if (field === "parentId" && this.task.parentType === "metric") {
      update.parentType = "metric";
    }

    this.tasksFacade
      .update$(this.task.id, update)
      .pipe(take(1))
      .subscribe((updatedTask) => {
        this.broadcastService.emit("taskUpdated", { task: updatedTask, update });
      });
  };
}
